import * as THREE from 'three';
import { G, M, TINT, HP, K_BEV, glowMat, mesh, bevel, plate, rib, mergeBones, damp, boneG } from './kit';
/* ============================================================
   TURM 塔 —— BOSS・攻城
   ============================================================

   剪影：14 單位高的三節塔
   二階段：塔身前傾至水平・改掃射（紫→橙）

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-06 TURM 塔 ── 14 單位高，全表最高的東西。
   P1 直立不動，純曲射（紫・範圍）→ 走位就好，但你打不到頂。
   P2 整座塔前傾到接近水平，變成貼地掃射的光束（橙・直射）→ 頂端下來了，
      弱點終於進得去，但地面變成不能站。 */
export function buildTurm(){
  var GLV = glowMat(TINT.vio.hex, 2.2), GLA = glowMat(TINT.amb.hex, 2.2);
  var root = new THREE.Group(), i;
  var base = boneG(root, 0, 0.70, 0);
  base.add(bevel(M.dark, 2.40, 1.20, 2.40, [0,0,0]));
  base.add(plate(M.armor, 2.60, 0.50, 1.20, [0, 0.40, 0.60], -0.34));
  for (i = 0; i < 3; i++){
    var a = i*Math.PI * 2/3 + 0.4;
    base.add(mesh(G.cyl, M.armor, [0.50,2.10,0.50],
      [Math.sin(a)*1.30, -0.44, Math.cos(a)*1.30], [0.60*Math.cos(a),0,-0.60*Math.sin(a)]));
    base.add(mesh(G.box, M.trim, [1.00,0.30,1.40],
      [Math.sin(a)*2.20, -1.36, Math.cos(a)*2.20], [0,-a,0]));
  }
  /* 三節塔身：每節都是骨段，P2 才折得下來 */
  var c1 = boneG(base, 0, 0.70, 0);
  c1.add(mesh(G.bev, M.armor, [1.70*K_BEV,3.60,1.70*K_BEV], [0,1.80,0]));
  c1.add(mesh(G.tor, M.trim,  [2.00,2.00,0.44], [0,3.44,0], [HP,0,0]));
  c1.add(rib(M.armor, 0.14, 3.20, 0.14, [0, 1.80, 0.90]));
  var c2 = boneG(c1, 0, 3.60, 0);
  c2.add(mesh(G.bev, M.armor, [1.40*K_BEV,3.30,1.40*K_BEV], [0,1.65,0]));
  c2.add(mesh(G.tor, M.trim,  [1.66,1.66,0.44], [0,3.16,0], [HP,0,0]));
  c2.add(plate(M.armor, 0.30, 2.60, 0.90, [ 0.82, 1.60, 0], 0, -0.14, -0.14));
  c2.add(plate(M.armor, 0.30, 2.60, 0.90, [-0.82, 1.60, 0], 0,  0.14,  0.14));
  var c3 = boneG(c2, 0, 3.30, 0);
  c3.add(mesh(G.bev, M.armor, [1.15*K_BEV,2.90,1.15*K_BEV], [0,1.45,0]));
  c3.add(rib(M.armor, 0.12, 2.60, 0.12, [0, 1.45, 0.64]));
  var head = boneG(c3, 0, 2.90, 0);
  head.add(bevel(M.dark, 1.50, 1.10, 1.60, [0, 0.42, 0]));
  head.add(plate(M.armor, 1.60, 0.44, 0.80, [0, 0.86, 0.40], -0.34));
  head.add(mesh(G.cone, M.armor, [0.90,1.60,0.90], [0, 0.50, 0.90], [1.05,0,0]));
  var muz = mesh(G.tor, GLV, [1.00,1.00,0.55], [0, 0.74, 1.56], [1.05,0,0]);
  muz.userData.noMerge = 1; head.add(muz);
  var beam = mesh(G.cyl, GLA, [0.44, 14.0, 0.44], [0, 0.74, 8.0], [HP,0,0]);
  beam.userData.noMerge = 1; beam.visible = false; head.add(beam);
  head.add(mesh(G.cone, M.trim, [0.40,1.20,0.40], [0, 1.40, -0.40]));

  root.userData.rig = {base:base, c1:c1, c2:c2, c3:c3, head:head, muz:muz, beam:beam,
                       GLV:GLV, GLA:GLA};
  return mergeBones(root);
}
export function updTurm(rig, o, dt){
  rig.fl = damp(rig.fl === undefined ? 0 : rig.fl, o.p2 ? 1 : 0, 1.8, dt);
  var f = rig.fl;
  var sway = 0.022*Math.sin(o.t*0.7);
  rig.c1.rotation.x = 1.02*f + sway - o.tel*0.05*(1-f);
  rig.c2.rotation.x = 0.26*f + sway*0.8;
  rig.c3.rotation.x = 0.08*f + sway*0.6;
  rig.head.rotation.x = -0.30*f + o.tel*0.26*(1-f) - o.atk*0.34*(1-f);
  rig.head.rotation.y = f ? Math.sin(o.t*0.9)*0.9*f : 0;
  rig.muz.scale.setScalar(1 + 0.10*Math.sin(o.t*2.4) + 0.9*o.tel + 1.6*o.atk*(1-o.atk));
  rig.beam.visible = f > 0.55 && o.atk > 0.02;
  if (rig.beam.visible){
    var k = Math.sin(o.atk*Math.PI);
    rig.beam.scale.set(0.44*(0.5+k), 14.0, 0.44*(0.5+k));
  }
  rig.GLV.emissiveIntensity = 2.2*(1-0.8*f) + 3.6*o.tel*(1-f) + 6*o.atk*(1-o.atk)*(1-f);
  rig.GLA.emissiveIntensity = 1.0 + 2.4*f + 6*o.atk*f;
  rig.base.rotation.z = 0.02*Math.sin(o.t*0.5);
}